import { PlusOutlined } from '@ant-design/icons';
import AppButton from '@nxsys/components/AppButton';

import { AppConfirmationDelete } from '@nxsys/components/AppConfirmationDeleteModal';
import { AppConfirmation } from '@nxsys/components/AppConfirmationModal';
import AppInputSearch from '@nxsys/components/AppInputSearch';
import AppsContent from '@nxsys/components/AppsContainer/AppsContent';
import AppTableContainer from '@nxsys/components/AppTableContainer';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppState } from '@nxsys/store';
import { companyActions } from '@nxsys/store/slices/company/reducer/companySlice';
import { GetWorkingPatternCompanyListParams } from '@nxsys/store/slices/company/types';
import { Icons } from '@nxsys/types/icons';
import { ICompany } from '@nxsys/types/models/company';
import { IWorkingPattern } from '@nxsys/types/models/workingPattern';
import { Switch, Tooltip } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { debounce, toNumber } from 'lodash';
import moment from 'moment';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import CreateWorkingPatternModal from './components/CreateWorkingPatternModal';
import {
  useCreateWorkingPattern,
  useDeleteWorkingPattern,
  useUpdateWorkingPattern,
} from '@nxsys/hooks/api/company';
import {
  CreateWorkingPatternParams,
  UpdateWorkingPatternParams,
} from '@nxsys/types/api/company';
import { WorkingPatternValues } from './components/WorkingPatternForm';
import { showMessage } from '@nxsys/utils/message';
import { DayOfWeekEnum } from '@nxsys/constants/AppEnums';
import EditWorkingPatternModal from './components/EditWorkingPatternModal';

type Props = {
  data?: ICompany;
};

const workingPatternMap = (workingPattern: DayOfWeekEnum[]) => {
  return {
    monday: workingPattern.includes(DayOfWeekEnum.MONDAY) ? 'Y' : 'N',
    tuesday: workingPattern.includes(DayOfWeekEnum.TUESDAY) ? 'Y' : 'N',
    wednesday: workingPattern.includes(DayOfWeekEnum.WEDNESDAY) ? 'Y' : 'N',
    thursday: workingPattern.includes(DayOfWeekEnum.THURSDAY) ? 'Y' : 'N',
    friday: workingPattern.includes(DayOfWeekEnum.FRIDAY) ? 'Y' : 'N',
    saturday: workingPattern.includes(DayOfWeekEnum.SATURDAY) ? 'Y' : 'N',
    sunday: workingPattern.includes(DayOfWeekEnum.SUNDAY) ? 'Y' : 'N',
  };
};

const WorkingPatternTab: React.FC<Props> = ({ data }) => {
  const { messages } = useTypedIntl();
  const dispatch = useDispatch();

  const { workingPatternCompanyList } = useSelector(
    (state: AppState) => state.companyStore,
  );
  const { loading, list, pagination, queryParams } = workingPatternCompanyList;

  useEffect(() => {
    if (data?.id) {
      handleFetchData({
        page: 0,
        size: 20,
      });
    }
  }, [data?.id]);

  const handleFetchData = (params: GetWorkingPatternCompanyListParams) => {
    const newParams = { ...params, companyId: data?.id || '' };
    dispatch(companyActions.getWorkingPatternCompanyList(newParams));
  };

  const refetch = () =>
    handleFetchData({
      ...queryParams,
    });

  const debouncedFetchData = useCallback(
    debounce((search: string) => {
      handleFetchData({
        ...queryParams,
        page: 0,
        size: 20,
        search: search,
      });
    }, 500),
    [queryParams],
  );

  const onChangePagination = (page: number, pageSize: number) => {
    const isPageSizeChanged = pageSize !== pagination.pageSize;
    const shouldResetPage = isPageSizeChanged && pagination.current > 0;
    handleFetchData({
      ...queryParams,
      page: shouldResetPage ? 0 : page - 1,
      size: pageSize,
    });
  };

  const onChangeTable = (sorter?: { field: string; order: 'desc' | 'asc' }) => {
    if (sorter?.order) {
      handleFetchData({
        ...queryParams,
        sortField: sorter.field ?? '',
        // NX-4948: the BE only recognises 'DESC' — lowercase sorted ascending.
        sortType: sorter.order === 'desc' ? 'DESC' : 'ASC',
      });
    } else {
      handleFetchData({
        ...queryParams,
        sortField: undefined,
        sortType: undefined,
      });
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    debouncedFetchData(value);
  };

  const columns: ColumnsType<IWorkingPattern> = [
    {
      key: 'action',
      title: messages['common.action'],
      fixed: 'left',
      align: 'left',
      render: (record) => (
        <ul className="flex items-center justify-start gap-x-2">
          <li onClick={() => toggleEditModal(record)}>
            <Icons.IconEdit />
          </li>
          <li onClick={() => handleDelete(record.id)}>
            <Icons.IconDelete />
          </li>
          {/* NX-4948: Old UI parity — set the company default from the
              list; the current default cannot be switched off here. */}
          <li>
            <Tooltip
              title={
                record.isDefault ? 'Already set as default' : 'Set as Default'
              }
            >
              <Switch
                size="small"
                checked={record.isDefault}
                disabled={record.isDefault}
                onChange={() => handleSetDefault(record)}
              />
            </Tooltip>
          </li>
        </ul>
      ),
      width: 140,
    },
    {
      key: 'name',
      title: messages['common.name'],
      dataIndex: 'name',
      sorter: true,
    },
    {
      key: 'workingPatternBinary',
      title: messages['common.workingPattern'],
      dataIndex: 'workingPatternBinary',
    },
    {
      key: 'workingDaysFTE',
      title: 'Working Days FTE',
      dataIndex: 'workingDaysFTE',
    },
    {
      key: 'workingDays',
      title: 'Working Days',
      dataIndex: 'workingDays',
    },
    {
      key: 'workingHoursFTE',
      title: 'Working Hours FTE',
      dataIndex: 'workingHoursFTE',
    },
    {
      key: 'workingHours',
      title: 'Working Hours',
      dataIndex: 'workingHours',
    },
    {
      key: 'payrollCalculationStart',
      title: 'Payroll Calculation Start Date',
      dataIndex: 'payrollCalculationStart',
      render: (value) => (value ? moment(value).format('DD/MM/YYYY') : '-'),
      width: 250,
    },
    {
      key: 'payrollCalculationEnd',
      title: 'Payroll Calculation End Date',
      dataIndex: 'payrollCalculationEnd',
      render: (value) => (value ? moment(value).format('DD/MM/YYYY') : '-'),
      width: 250,
    },
    {
      key: 'createdAt',
      title: 'Created At',
      dataIndex: 'createdAt',
      render: (value) => (value ? moment(value).format('DD/MM/YYYY') : '-'),
    },
    {
      key: 'createdBy',
      title: 'Created By',
      dataIndex: 'createdBy',
      render: (value) =>
        value ? `${value?.forename || ''} ${value?.surname || ''}` : '-',
    },
  ];

  const [isOpenCreateModal, setIsOpenCreateModal] = useState(false);
  const { run: createWorkingPattern } = useCreateWorkingPattern();
  const handleCreateWorkingPattern = async (values: WorkingPatternValues) => {
    const params: CreateWorkingPatternParams = {
      name: values.name,
      isDefault: values.isDefault,
      workingPattern: workingPatternMap(
        values.workingPattern?.map((item) => item.value as DayOfWeekEnum) || [],
      ),
      workingDaysFTE: values.workingDaysFTE
        ? toNumber(values.workingDaysFTE)
        : null,
      workingDays: values.workingDays ? toNumber(values.workingDays) : null,
      workingHoursFTE: values.workingHoursFTE
        ? toNumber(values.workingHoursFTE)
        : null,
      workingHours: values.workingHours ? toNumber(values.workingHours) : null,
      companyId: data?.id || '',
    };
    const res = await createWorkingPattern(params);
    if (res.success) {
      showMessage({
        type: 'success',
        content: messages['common.addSuccess'],
      });
      setIsOpenCreateModal(false);
      refetch();
    }
  };

  const [isOpenEditModal, setIsOpenEditModal] = useState(false);
  const [selectedData, setSelectedData] = useState<IWorkingPattern | null>(
    null,
  );
  const toggleEditModal = (data?: IWorkingPattern) => {
    setSelectedData(data || null);
    setIsOpenEditModal(true);
  };
  const { run: updateWorkingPattern } = useUpdateWorkingPattern();
  const handleEditWorkingPattern = async (values: WorkingPatternValues) => {
    if (!selectedData?.id) return;
    const params: UpdateWorkingPatternParams = {
      name: values.name,
      isDefault: values.isDefault,
      workingPattern: workingPatternMap(
        values.workingPattern?.map((item) => item.value as DayOfWeekEnum) || [],
      ),
      workingDaysFTE: values.workingDaysFTE
        ? toNumber(values.workingDaysFTE)
        : null,
      workingDays: values.workingDays ? toNumber(values.workingDays) : null,
      workingHoursFTE: values.workingHoursFTE
        ? toNumber(values.workingHoursFTE)
        : null,
      workingHours: values.workingHours ? toNumber(values.workingHours) : null,
    };
    const res = await updateWorkingPattern(selectedData.id, params);
    if (res.success) {
      showMessage({
        type: 'success',
        content: messages['common.updateSuccess'],
      });
      setIsOpenEditModal(false);
      refetch();
    }
  };

  // The BE unsets the previous default when one is set (update endpoint).
  const handleSetDefault = (record: IWorkingPattern) => {
    AppConfirmation.open({
      title: messages['common.setAsWorkingPatternDefault'],
      paragraph: `Are you sure you want to set the working pattern "${record.name}" as default?`,
      onConfirm: async () => {
        // The update endpoint is partial: omitted fields keep their stored
        // values and it unsets the previous default first, so isDefault is
        // all it needs.
        const res = await updateWorkingPattern(record.id, { isDefault: true });
        if (res?.success) {
          showMessage({
            type: 'success',
            content: messages['common.updateSuccess'],
          });
          refetch();
        }
      },
    });
  };

  const { run: deleteWorkingPattern } = useDeleteWorkingPattern();
  const handleDelete = async (id: number) => {
    AppConfirmationDelete.open({
      title: messages['common.delete'],
      paragraph: messages['common.deleteConfirm'],
      onConfirm: async () => {
        const res = await deleteWorkingPattern(id);
        if (res.success) {
          showMessage({
            type: 'success',
            content: messages['common.deleteSuccess'],
          });
        }
        refetch();
      },
    });
  };

  return (
    <>
      <div className="p-[16px]">
        <div className="flex justify-between items-center mb-[16px]">
          <AppInputSearch onChange={handleSearchChange} />
          <AppButton
            type="primary"
            name={messages['common.addNew']}
            leftIcon={<PlusOutlined />}
            onClick={() => setIsOpenCreateModal(true)}
          />
        </div>
        <AppsContent>
          <AppTableContainer
            data={list ?? []}
            loading={loading}
            page={pagination.current}
            total={pagination.totalDocs}
            pageSize={pagination.pageSize}
            scroll={{ x: 2000, y: 700 }}
            titleEmpty={messages['common.noData']}
            columns={columns}
            onChangePagination={onChangePagination}
            onChangeTable={onChangeTable}
          />
        </AppsContent>
      </div>
      <CreateWorkingPatternModal
        isOpen={isOpenCreateModal}
        toggle={() => setIsOpenCreateModal(false)}
        onSubmit={handleCreateWorkingPattern}
      />
      {selectedData && isOpenEditModal && (
        <EditWorkingPatternModal
          isOpen={isOpenEditModal}
          toggle={() => setIsOpenEditModal(false)}
          onSubmit={handleEditWorkingPattern}
          data={selectedData}
        />
      )}
    </>
  );
};

export default WorkingPatternTab;
